import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  FileText, Download, ChevronDown, ChevronUp, Search, X,
  ClipboardCheck, AlertTriangle, Wrench, Users, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (v) => {
  if (!v || typeof v !== 'string') return v;
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return v;
};

const DATE_COLS = new Set(['fecha', 'fecha_compromiso', 'fecha_gcom']);

const REPORTES = [
  { id: 'cumplimiento', nombre: 'Reporte de Cumplimiento GCOM', desc: 'Detalle de todos los GCOMs con estado, participantes, accidentes y hallazgos', icon: ClipboardCheck, color: 'bg-teal-100 text-teal-700' },
  { id: 'accidentes', nombre: 'Reporte de Accidentes y Hallazgos', desc: 'GCOMs donde se reportaron accidentes o hallazgos, con peligros y controles', icon: AlertTriangle, color: 'bg-red-100 text-red-700' },
  { id: 'acciones', nombre: 'Reporte de Acciones Correctivas', desc: 'Acciones pendientes y cerradas con responsables y fechas de compromiso', icon: Wrench, color: 'bg-amber-100 text-amber-700' },
  { id: 'participacion', nombre: 'Reporte de Participación', desc: 'Ranking de participantes por cantidad de asistencias a GCOMs', icon: Users, color: 'bg-indigo-100 text-indigo-700' },
  { id: 'peligros', nombre: 'Reporte de Peligros y Controles', desc: 'Ranking de peligros identificados con actividades y controles asociados', icon: Shield, color: 'bg-purple-100 text-purple-700' },
];

const COLUMNS = {
  cumplimiento: ['id', 'fecha', 'turno', 'tipo', 'codigo', 'estado', 'participantes', 'accidentes', 'hallazgos', 'mejoras'],
  accidentes: ['id', 'fecha', 'turno', 'codigo', 'detalle_accidente', 'detalle_hallazgos', 'actividad', 'peligro', 'controles'],
  acciones: ['id', 'accion', 'responsable', 'fecha_compromiso', 'estado', 'fecha_gcom', 'codigo_gcom'],
  participacion: ['nombre', 'cargo', 'rut', 'asistencias'],
  peligros: ['peligro', 'actividad', 'controles', 'frecuencia'],
};

const HEADERS = {
  id: 'ID', fecha: 'Fecha', turno: 'Turno', tipo: 'Tipo', codigo: 'Código', estado: 'Estado',
  participantes: 'Participantes', accidentes: 'Accidentes', hallazgos: 'Hallazgos', mejoras: 'Mejoras',
  detalle_accidente: 'Detalle Accidente', detalle_hallazgos: 'Detalle Hallazgos',
  actividad: 'Actividad', peligro: 'Peligro', controles: 'Controles',
  accion: 'Acción', responsable: 'Responsable', fecha_compromiso: 'Fecha Compromiso',
  fecha_gcom: 'Fecha GCOM', codigo_gcom: 'Código GCOM',
  nombre: 'Nombre', cargo: 'Cargo', rut: 'RUT', asistencias: 'Asistencias', frecuencia: 'Frecuencia'
};

export const ReportesGCOM = () => {
  const { token } = useAuth();
  const [active, setActive] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 15;
  const headers = { Authorization: `Bearer ${token}` };

  const loadReport = async (tipo) => {
    if (active === tipo) { setActive(null); setReportData(null); return; }
    setLoading(true);
    setSearch('');
    setPage(1);
    try {
      const res = await axios.get(`${API}/gcom/reporte/${tipo}`, { headers });
      setReportData(res.data);
      setActive(tipo);
    } catch (e) {
      toast.error('Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!reportData) return;
    const cols = COLUMNS[reportData.tipo];
    const csvHeader = cols.map(c => HEADERS[c]).join(',');
    const csvRows = reportData.data.map(row =>
      cols.map(c => {
        const val = String(row[c] || '').replace(/"/g, '""');
        return `"${val}"`;
      }).join(',')
    );
    const csv = [csvHeader, ...csvRows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_gcom_${reportData.tipo}_${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' }).split('-').reverse().join('-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Reporte descargado');
  };

  const filtered = reportData?.data?.filter(row => {
    if (!search) return true;
    const s = search.toLowerCase();
    return Object.values(row).some(v => String(v).toLowerCase().includes(s));
  }) || [];

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="space-y-6" data-testid="reportes-gcom-page">
      <h1 className="text-2xl font-bold text-slate-900">Reportes GCOM</h1>

      <div className="space-y-3">
        {REPORTES.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => loadReport(r.id)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50/50 transition-colors"
              data-testid={`report-btn-${r.id}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${r.color}`}>
                  <r.icon className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">{r.nombre}</p>
                  <p className="text-xs text-slate-500">{r.desc}</p>
                </div>
              </div>
              {loading && active === null ? (
                <div className="w-5 h-5 border-2 border-slate-300 border-t-teal-500 rounded-full animate-spin" />
              ) : active === r.id ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>

            {active === r.id && reportData && (
              <div className="border-t border-slate-200 px-5 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div className="relative max-w-sm flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Filtrar resultados..."
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                      className="pl-10 pr-10"
                      data-testid="report-search"
                    />
                    {search && (
                      <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">{filtered.length} registros</span>
                    <Button onClick={downloadCSV} variant="outline" size="sm" data-testid="download-csv">
                      <Download className="w-4 h-4 mr-1.5" /> Descargar CSV
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm" data-testid="report-table">
                    <thead>
                      <tr className="bg-slate-50">
                        {COLUMNS[reportData.tipo].map(col => (
                          <th key={col} className="text-left px-3 py-2.5 font-semibold text-slate-600 whitespace-nowrap text-xs">
                            {HEADERS[col]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((row, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/50">
                          {COLUMNS[reportData.tipo].map(col => (
                            <td key={col} className={`px-3 py-2 text-slate-700 ${col === 'estado' ? (row[col] === 'Pendiente' ? 'text-amber-600 font-medium' : row[col] === 'Cerrada' ? 'text-green-600 font-medium' : '') : ''} max-w-xs truncate`}>
                              {DATE_COLS.has(col) ? fmtDate(row[col]) : (row[col] ?? '-')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm text-slate-500">
                      {(page - 1) * perPage + 1}-{Math.min(page * perPage, filtered.length)} de {filtered.length}
                    </span>
                    <div className="flex gap-1">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                        className="px-3 py-1.5 text-sm rounded-md border border-slate-200 hover:bg-slate-100 disabled:opacity-40">
                        Anterior
                      </button>
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                        className="px-3 py-1.5 text-sm rounded-md border border-slate-200 hover:bg-slate-100 disabled:opacity-40">
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportesGCOM;
